import { getCSS, remCSS } from '@gimlite/common';
import classNames from 'classnames';
import { memo, useId } from 'react';
import { ColorType } from '../../types/component.type';
import { Icon, IconType } from '../icon/icon.component';
import { Text } from '../text/text.component';
import './button.component.scss';

const sizeMapper = {
  small: {
    padding: `${remCSS(8)} ${remCSS(12)}`,
    borderRadius: '10px',
  },
  medium: {
    padding: `${remCSS(12)} ${remCSS(16)}`,
    borderRadius: '10px',
  },
  large: {
    padding: `${remCSS(16)} ${remCSS(16)}`,
    borderRadius: '6px',
  },
} as const satisfies Record<string, { padding: string; borderRadius: string }>;

const variantColorMapper = {
  tertiary: {
    textColor: 'white_100',
    bgColor: 'white_30',
    borderColor: null,
  },
  primary: {
    textColor: 'white_100',
    bgColor: 'soft_teal',
    borderColor: 'soft_teal',
  },
  secondary: {
    textColor: 'white_100',
    bgColor: 'black_70',
    borderColor: 'black_70',
  },
  action: {
    textColor: 'white_100',
    bgColor: 'deep_indigo',
    borderColor: null,
  },
  outline: {
    textColor: 'deep_indigo',
    bgColor: null,
    borderColor: 'deep_indigo',
  },
  ghost: {
    textColor: 'deep_indigo',
    bgColor: null,
    borderColor: null,
  },
  alert_warning: {
    textColor: 'white_100',
    bgColor: 'alert_warning',
    borderColor: 'alert_warning',
  },
  alert_advisory: {
    textColor: 'deep_indigo',
    bgColor: 'alert_advisory',
    borderColor: 'alert_advisory',
  },
  alert_error: {
    textColor: 'white_100',
    bgColor: 'alert_error',
    borderColor: 'alert_error',
  },
} as const satisfies Record<
  string,
  {
    textColor: ColorType;
    bgColor: ColorType | null;
    borderColor: ColorType | null;
  }
>;

export declare namespace ButtonType {
  type Props = {
    className?: string | null;
    children?: string | null;
    iconLeft?: IconType.Props['variant'] | null;
    iconRight?: IconType.Props['variant'] | null;
    variant?: keyof typeof variantColorMapper;
    size?: keyof typeof sizeMapper;
    onClick?: () => void;
    stopPropagation?: boolean;
    width?: 'min-content' | 'max-content';
    justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between';
    minWidth?: boolean;
    type?:
      | { name: 'button' }
      | { name: 'submit' }
      | {
          name: 'upload';
          onUpload?: (event: FileList) => void;
          multiple?: boolean;
          accept?: string;
        };
    disabled?: boolean;
  };
}

export const Button = memo(
  ({
    children,
    className,
    iconLeft,
    iconRight,
    variant = 'primary',
    onClick,
    width = 'min-content',
    minWidth = false,
    stopPropagation = true,
    justifyContent = 'center',
    type = { name: 'button' },
    disabled = false,
    size = 'medium',
  }: ButtonType.Props) => {
    const id = useId();
    return (
      <div
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          if (disabled) return;
          onClick?.();
        }}
        style={{
          backgroundColor: variantColorMapper[variant].bgColor
            ? getCSS(`--color-${variantColorMapper[variant].bgColor}`)
            : 'transparent',
          borderWidth: variantColorMapper[variant].borderColor ? '1px' : '0px',
          borderColor: variantColorMapper[variant].borderColor
            ? getCSS(`--color-${variantColorMapper[variant].borderColor}`)
            : 'none',
          padding: sizeMapper[size].padding,
          borderRadius: sizeMapper[size].borderRadius,
          width: width === 'min-content' ? 'min-content' : '100%',
          justifyContent,
        }}
        className={classNames('button', className, {
          'button--disabled': disabled,
          'button--minWidth': minWidth,
        })}
      >
        {type.name !== 'upload' && (
          <button className="button__native" type={type.name}></button>
        )}
        {type.name === 'upload' && (
          <label className="button__upload" htmlFor={`${id}-upload`}>
            <input
              id={`${id}-upload`}
              type="file"
              value={''}
              accept={type.accept}
              multiple={type.multiple || false}
              onChange={(e) => {
                const filesList = e?.target?.files;
                if (filesList) type.onUpload?.(filesList);
              }}
            ></input>
          </label>
        )}
        {iconLeft && (
          <Icon
            className="button__icon"
            variant={iconLeft}
            color={variantColorMapper[variant].textColor}
          />
        )}
        {children && (
          <Text
            className="button__text"
            variant={size === 'small' ? 'subhead' : 'button-s'}
            color={variantColorMapper[variant].textColor}
            whiteSpace="nowrap"
          >
            {children}
          </Text>
        )}
        {iconRight && (
          <Icon
            className="button__icon"
            variant={iconRight}
            color={variantColorMapper[variant].textColor}
          />
        )}
      </div>
    );
  },
);
